/**
 * Bulk import sender — fires one POST per validated row against the
 * existing single-record endpoints. Uses a small concurrency window so a
 * 200-row import doesn't open 200 sockets at once but also doesn't crawl
 * sequentially.
 *
 * No backend changes required: each row maps onto the standard
 * TripCreate / EnergyEventCreate Pydantic schemas.
 */
import { TripsAPI, EnergyAPI } from "@/lib/api";
import type { ImportOutcome, RowValidation } from "./types";

const CONCURRENCY = 6;

interface SendOptions {
  kind: "trip" | "charging" | "fuel";
  vehicleId: string;
  rows: RowValidation[];
}

/** Convert a validated row → backend payload for the given kind. */
const buildPayload = (kind: SendOptions["kind"], vehicleId: string, v: Record<string, unknown>) => {
  if (kind === "trip") {
    const startOdo = v.start_odometer as number | undefined;
    const endOdo = v.end_odometer as number | undefined;
    return {
      vehicle_id: vehicleId,
      date: v.date as string,
      distance: v.distance as number,
      unit: ((v.unit as string) || "km").toLowerCase() === "mi" ? "mi" : "km",
      notes: (v.notes as string) || "",
      start_odometer_km: typeof startOdo === "number" ? startOdo : undefined,
      end_odometer_km: typeof endOdo === "number" ? endOdo : undefined,
      start_battery_pct: typeof v.start_battery_pct === "number" ? (v.start_battery_pct as number) : undefined,
      end_battery_pct: typeof v.end_battery_pct === "number" ? (v.end_battery_pct as number) : undefined,
    };
  }
  if (kind === "charging") {
    return {
      vehicle_id: vehicleId,
      type: "charge",
      date: v.date as string,
      amount: (v.amount as number) ?? 0,
      cost: (v.cost as number) ?? 0,
      unit: "kWh",
      price_per_unit: typeof v.price_per_unit === "number" ? (v.price_per_unit as number) : undefined,
      odometer_km: typeof v.odometer === "number" ? (v.odometer as number) : undefined,
      start_battery_pct: typeof v.start_battery_pct === "number" ? (v.start_battery_pct as number) : undefined,
      end_battery_pct: typeof v.end_battery_pct === "number" ? (v.end_battery_pct as number) : undefined,
      notes: (v.notes as string) || "",
    };
  }
  // fuel
  const unit = ((v.unit as string) || "L").toLowerCase();
  return {
    vehicle_id: vehicleId,
    type: "fuel",
    date: v.date as string,
    amount: (v.amount as number) ?? 0,
    cost: (v.cost as number) ?? 0,
    unit: unit.startsWith("gal") ? "gal" : "L",
    price_per_unit: typeof v.price_per_unit === "number" ? (v.price_per_unit as number) : undefined,
    odometer_km: typeof v.odometer === "number" ? (v.odometer as number) : undefined,
    notes: (v.notes as string) || "",
  };
};

const extractServerError = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return err?.message || "Server rejected the row";
};

/** Send validated rows. Returns aggregate outcome with row-level failures. */
export async function sendRows({ kind, vehicleId, rows }: SendOptions): Promise<ImportOutcome> {
  const errors: ImportOutcome["errors"] = [];
  let inserted = 0;

  let cursor = 0;
  const next = async (): Promise<void> => {
    while (cursor < rows.length) {
      const i = cursor++;
      const row = rows[i];
      try {
        const payload = buildPayload(kind, vehicleId, row.values);
        if (kind === "trip") await TripsAPI.create(payload);
        else await EnergyAPI.create(payload);
        inserted++;
      } catch (e) {
        errors.push({ rowNumber: row.rowNumber, reason: extractServerError(e) });
      }
    }
  };
  // Spin up CONCURRENCY workers, share the cursor.
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, rows.length) }, next));
  return { inserted, errors };
}
